import { formatTrackingTimeIST } from 'utils/tripNodeStatus';

/**
 * Display helpers for the Distance tab.
 *
 * Every one of them renders `null` as an em dash, never as `0`. A zero on this tab is a
 * measurement; a dash is the absence of one, and the whole point of the tab is that the
 * two are never confused.
 */

export const EMPTY = '—';

/** `65` → `65 km`, `71.5` → `71.5 km`. Whole numbers stay whole; nothing reads as `70.0`. */
export const formatKm = (value) => {
	if (value === null || value === undefined) return EMPTY;
	const n = Number(value);
	return `${Number.isInteger(n) ? n : n.toFixed(1)} km`;
};

export const formatPercent = (value, digits = 0) =>
	value === null || value === undefined ? EMPTY : `${Number(value).toFixed(digits)}%`;

export const formatReading = (value) =>
	value === null || value === undefined ? EMPTY : Number(value).toLocaleString('en-IN');

export const formatReadingKm = (value) => {
	const reading = formatReading(value);
	return reading === EMPTY ? EMPTY : `${reading} km`;
};

/** `8040` → `2h 14m`. Sub-minute durations round up to `<1m` rather than showing `0m`. */
export const formatDuration = (seconds) => {
	if (seconds === null || seconds === undefined) return EMPTY;
	const total = Math.max(0, Math.round(Number(seconds)));
	// Zero is a measured absence — distinct from `—` (never measured) and `<1m` (under a minute).
	if (total === 0) return '0m';
	if (total < 60) return '<1m';

	const days = Math.floor(total / 86400);
	const hours = Math.floor((total % 86400) / 3600);
	const minutes = Math.floor((total % 3600) / 60);

	const parts = [];
	if (days) parts.push(`${days}d`);
	if (hours) parts.push(`${hours}h`);
	if (minutes || !parts.length) parts.push(`${minutes}m`);
	return parts.join(' ');
};

/**
 * Multi-day trips need the date on the axis; a single-day trip does not and reads better
 * without it. `spansDays` comes from the trip window, not from the individual timestamp.
 */
export const formatTimestamp = (value, spansDays = false) =>
	formatTrackingTimeIST(value, spansDays ? 'DD MMM, hh:mm A' : 'hh:mm A');

export const formatCoordinates = (lat, lng) =>
	lat === null || lng === null || lat === undefined || lng === undefined
		? EMPTY
		: `${Number(lat).toFixed(5)}, ${Number(lng).toFixed(5)}`;
